import { defineMessages, translator, type Locale } from "../i18n/index.ts";

/**
 * Questions proposées pour ouvrir la discussion sur un livre, comme ChatNovel et Studio (dépôt ai,
 * `components/studio/questionSuggestions.ts`) : qui compte dans le livre, puis son contexte, selon sa catégorie
 * (premier niveau du catalogue). Rien sur ce qui n'a pas encore été lu : le récap passe avant elles quand il est
 * proposé. Le back-office peut en choisir d'autres pour une catégorie, parmi ces textes (leurs clés) ou écrites
 * par lui, posées telles quelles.
 */
const messages = defineMessages(
  {
    whoMatters: "Qui sont les personnages importants ?",
    storyContext: "Quel est le contexte de cette histoire (époque, lieu, auteur) ?",
    importantIdeas: "Quelles sont les idées importantes de ce livre ?",
    bookContext: "Quel est le contexte de ce livre (époque, lieu, auteur) ?",
    inquiryContext: "Quel est le contexte de cette enquête (époque, lieu, auteur) ?",
    universe: "Dans quel univers se déroule cette histoire ?",
    essentialNotions: "Quelles sont les notions essentielles à retenir ?",
    studiedWork: "Sur quelle œuvre ou quel programme porte ce livre ?",
    mustSee: "Quels sont les incontournables ?",
    beforeLeaving: "Que faut-il savoir avant de partir (saison, climat, transports) ?",
    signatureRecipes: "Quelles sont les recettes phares de ce livre ?",
    cuisine: "Quelle cuisine ce livre présente-t-il (région, style, auteur) ?",
    essentialAdvice: "Quels sont les conseils essentiels de ce livre ?",
    approach: "Sur quelle approche ce livre s’appuie-t-il (méthode, auteur) ?",
    audience: "À qui s’adresse ce livre, et par où commencer ?",
    artists: "Quels sont les artistes et les œuvres importants ?",
    artsContext: "Quel est le contexte de ce livre (époque, mouvement, auteur) ?",
    debateContext: "Quel est le contexte de ce livre (époque, débat, auteur) ?",
    historyContext: "Quel est le contexte de ce livre (époque, lieux, auteur) ?",
    importantNotions: "Quelles sont les notions importantes de ce livre ?",
    disciplineContext: "Quel est le contexte de ce livre (discipline, époque, auteur) ?",
    fieldContext: "Quel est le contexte de ce livre (domaine, époque, auteur) ?",
    teachings: "Quels sont les enseignements importants de ce livre ?",
    traditionContext: "Quel est le contexte de ce livre (tradition, époque, auteur) ?",
  },
  {
    whoMatters: "Who are the main characters?",
    storyContext: "What is the context of this story (period, setting, author)?",
    importantIdeas: "What are the key ideas of this book?",
    bookContext: "What is the context of this book (period, place, author)?",
    inquiryContext: "What is the context of this investigation (period, setting, author)?",
    universe: "In what world does this story take place?",
    essentialNotions: "What are the essential notions to remember?",
    studiedWork: "Which work or syllabus is this book about?",
    mustSee: "What are the must-sees?",
    beforeLeaving: "What should I know before I go (season, climate, transport)?",
    signatureRecipes: "What are the signature recipes of this book?",
    cuisine: "What cuisine does this book present (region, style, author)?",
    essentialAdvice: "What is the essential advice of this book?",
    approach: "What approach is this book based on (method, author)?",
    audience: "Who is this book for, and where should I start?",
    artists: "Who are the important artists and works?",
    artsContext: "What is the context of this book (period, movement, author)?",
    debateContext: "What is the context of this book (period, debate, author)?",
    historyContext: "What is the context of this book (period, places, author)?",
    importantNotions: "What are the key notions of this book?",
    disciplineContext: "What is the context of this book (discipline, period, author)?",
    fieldContext: "What is the context of this book (field, period, author)?",
    teachings: "What are the key teachings of this book?",
    traditionContext: "What is the context of this book (tradition, period, author)?",
  },
);

type Question = keyof (typeof messages)["fr"];

const STORY: Question[] = ["whoMatters", "storyContext"];

export const GENERAL_QUESTIONS: Question[] = ["importantIdeas", "bookContext"];

/** Par catégorie du catalogue, telles que le backend les nomme (en français). */
export const QUESTIONS_BY_CATEGORY: Record<string, Question[]> = {
  Littérature: STORY,
  "Policier & Thriller": ["whoMatters", "inquiryContext"],
  Romance: STORY,
  "Fantasy & SF": ["whoMatters", "universe"],
  "BD & Comics": STORY,
  "Manga / Manhwa / Man Hua": STORY,
  Jeunesse: STORY,
  Parascolaire: ["essentialNotions", "studiedWork"],
  "Tourisme & Voyages": ["mustSee", "beforeLeaving"],
  Cuisine: ["signatureRecipes", "cuisine"],
  "Santé & Bien-être": ["essentialAdvice", "approach"],
  "Vie pratique & Loisirs": ["essentialAdvice", "audience"],
  "Arts & Spectacles": ["artists", "artsContext"],
  "Idées & Société": ["importantIdeas", "debateContext"],
  Histoire: ["whoMatters", "historyContext"],
  "Sciences humaines et sociales": ["importantNotions", "disciplineContext"],
  "Sciences & Techniques": ["importantNotions", "fieldContext"],
  "Entreprise, Économie & Droit": ["importantIdeas", "fieldContext"],
  "Religion & Spiritualité": ["teachings", "traditionContext"],
};

const isQuestionKey = (key: string): key is Question => Object.hasOwn(messages.fr, key);

/** Les questions de départ d'un livre, dans la langue de l'interface. */
export function questionSuggestions(category: string | null | undefined, locale: Locale, chosen?: string[] | null): string[] {
  const t = translator(messages, locale);
  const given = (chosen ?? []).map((item) => item.trim()).filter(Boolean);
  if (given.length) return given.map((item) => (isQuestionKey(item) ? t(item) : item));
  const byCategory = category && Object.hasOwn(QUESTIONS_BY_CATEGORY, category) ? QUESTIONS_BY_CATEGORY[category] : undefined;
  return (byCategory ?? GENERAL_QUESTIONS).map((key) => t(key));
}
